import type { DropDownListChangeEvent } from "@tql/ui-components-react/kendo/dropdowns";
import {
	Card,
	CardBody,
	CardHeader,
} from "@tql/ui-components-react/kendo/layout";
import { DropDownList } from "@tql/ui-components-react/tql/dropdowns";
import { useAtomValue } from "jotai";
import { useMemo } from "react";

import { useBillingCustomers } from "~/features/quote/hooks/use-billing-customers.ts";
import { getSearchCustomerRoute } from "~/features/quote/hooks/use-quote-details-hooks.ts";
import { SearchCombobox } from "~/shared/components/fields/search-combobox.tsx";
import type { BillingCustomerAssociation } from "~/shared/schemas/billing-customer-schema.ts";
import type { Customer } from "~/shared/schemas/customer-schema.ts";
import { customerSchema } from "~/shared/schemas/customer-schema.ts";
import type {
	ShipmentCustomer,
	ShipmentLineItemCost,
} from "~/shared/schemas/shipment-management-schema";
import type { OnChange } from "~/shared/types/quote-details-types";

import { useCustomerDraft } from "../../hooks/use-shipment-draft.ts";
import { isEditingShipmentAtom } from "../../state/shipment-edit-atoms.ts";
import { CUSTOMER_DEFAULT } from "../../utils/defaults.ts";
import { LineItemCost } from "../line-item-cost/line-item-cost.tsx";

export const CustomerCard = ({
	details = CUSTOMER_DEFAULT,
}: {
	details?: ShipmentCustomer;
}) => {
	const isEditing = useAtomValue(isEditingShipmentAtom);
	// Customer editing remains disabled until the future enhancement is implemented.
	const isCustomerFieldEditable = false;
	const { customer: customerDraft, updateCustomer } = useCustomerDraft();
	// While editing, show the draft values so edits are reflected live; fall back
	// to the passed-in details before a draft has been seeded.
	const current = customerDraft ?? details;
	const { data: billingCustomers } = useBillingCustomers(current.id);
	const tenderedCustomerId = current.id ?? current.tenderedCustomer?.customerId;
	const billingCustomerId = current.billingCustomer?.customerId;

	const billingCustomerOptions = useMemo(
		() =>
			(billingCustomers ?? []).filter(
				(billingCustomer) => billingCustomer.customerId !== tenderedCustomerId,
			),
		[billingCustomers, tenderedCustomerId],
	);

	const selectedBillingCustomer = useMemo(
		() =>
			billingCustomerOptions.find(
				(billingCustomer) => billingCustomer.customerId === billingCustomerId,
			) ?? null,
		[billingCustomerId, billingCustomerOptions],
	);

	const showBillingCustomerSection = isEditing
		? billingCustomerOptions.length > 0
		: billingCustomerId != null &&
			billingCustomerId !== tenderedCustomerId &&
			Boolean(current.billingCustomer?.customerName?.trim());

	const hasTotalCharges = details?.totalCharges != null;
	const total = useMemo(() => {
		if (details?.totalCharges != null) {
			return details.totalCharges;
		}
		// The external shipment endpoint still returns itemized charges without totalCharges.
		return (
			details?.charges?.reduce((acc, charge) => {
				if (charge.description?.toLowerCase() === "discount") {
					return acc - charge.amount;
				}
				return acc + charge.amount;
			}, 0) ?? 0
		);
	}, [details]);
	return (
		<div
			className={"half-size-card"}
			data-testid={"shipment-management-customer"}
		>
			<Card className={"full-height shipment-info-card"}>
				<CardHeader>
					<h3>Customer</h3>
				</CardHeader>
				<CardBody>
					<div className={"party-details"}>
						<div className={"info-field"}>
							<p className={"section-label"}>Tendered From:</p>
							{isCustomerFieldEditable ? (
								<SearchCombobox
									dataItemKey={"id"}
									handleChange={(event) => {
										const value = (event as { value?: OnChange })?.value;
										const newCustomer = value as Customer | undefined;
										updateCustomer({
											id: newCustomer?.id ?? 0,
											customerName: newCustomer?.name ?? null,
											// Billing customers depend on the selected customer, so
											// reset the choice when the customer changes.
											billingCustomer: null,
										});
									}}
									hint={false}
									hintDirection={undefined}
									id={"customer"}
									label={""}
									minSearchLength={3}
									name={"customer"}
									placeholder={
										"Type at least 3 characters to search customers..."
									}
									route={getSearchCustomerRoute}
									schema={customerSchema}
									textField={"nameWithId"}
									value={{
										id: current.id,
										nameWithId: current.customerName ?? "",
									}}
									wrapperStyle={{ marginTop: "0px", minWidth: 275 }}
								/>
							) : (
								<p data-testid={"customer-name"}>
									{current.customerName}
									{current.id == null ? "" : ` (${current.id})`}
								</p>
							)}
						</div>
						{showBillingCustomerSection ? (
							<div className={"info-field"}>
								<p className={"section-label"}>Billing Customer</p>
								{isEditing ? (
									<DropDownList
										data={billingCustomerOptions}
										dataItemKey={"customerId"}
										defaultItem={{
											customerId: null,
											customerName: "",
											ftCustomerId: 0,
										}}
										onChange={(event: DropDownListChangeEvent) => {
											const value =
												event.value as BillingCustomerAssociation | null;
											updateCustomer({
												billingCustomer: value
													? {
															customerId: value.customerId,
															customerName: value.customerName,
														}
													: null,
											});
										}}
										style={{ width: 350 }}
										textField={"customerName"}
										value={selectedBillingCustomer}
									/>
								) : (
									<p>{current.billingCustomer?.customerName}</p>
								)}
							</div>
						) : null}
					</div>
					<div className={"rate-summary"}>
						<p className={"section-label"}>Customer Rate</p>
						<div>
							{hasTotalCharges ? (
								<LineItemCost
									label={"Freight Rate"}
									value={`$${total.toFixed(2)}`}
								/>
							) : (
								details.charges?.map((charge: ShipmentLineItemCost) => (
									<LineItemCost
										key={`${charge.description}-${charge.amount}`}
										label={charge.description ?? "Misc Charge"}
										value={`$${charge.amount.toFixed(2).toLocaleString()}`}
									/>
								))
							)}
						</div>
						<div className={"price-line-item rate-total"}>
							<p>Total</p>
							<p data-testid={"customer-total"}>{`$${total.toFixed(2)}`}</p>
						</div>
					</div>
				</CardBody>
			</Card>
		</div>
	);
};
